import { ApiCompetitionsGet200ResponseDataCompetitions } from '@/api/livescore/generated';
import { DividerCard, DividerCardBody, DividerCardTitle } from '@/components/atoms/DividerCard';
import { Input } from '@/components/atoms/Input';
import { cn } from '@/utils/cn';
import { convertAlpha, getCountryName } from '@/utils/countryCode';
import { Disclosure } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/solid';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import { useLivescoreLayout } from '@/context/LivescoreLayoutContext';
import LivescoreEntityLink from '@/components/atoms/LivescoreEntityLink/LivescoreEntityLink';

interface CompetitionCollapseProps {
  countryCode?: string;
  competitions: ApiCompetitionsGet200ResponseDataCompetitions['all'][string];
}

const CompetitionCountryCollapse = ({ countryCode, competitions }: CompetitionCollapseProps) => {
  const [open, setOpen] = useState(false);

  const countryName = (countryCode && getCountryName(countryCode)) ?? 'Międzynarodowe';
  const countryCodeAlpha2 = (countryCode && convertAlpha(countryCode)) ?? 'un';

  return (
    <Disclosure>
      <div
        className={cn(
          'text-black dark:text-white rounded-lg overflow-hidden border transition-colors',
          open ? 'dark:border-gray-550 border-white_theme-100' : 'dark:border-gray-550/0 border-white_theme-100/0',
        )}
      >
        <Disclosure.Button
          onClick={() => setOpen(!open)}
          className="dark:bg-gray-650 bg-white_theme-50 group w-full px-4 py-2 transition-all duration-300 relative"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="h-6 w-6 relative rounded-full overflow-hidden">
                <Image
                  src={`https://flagcdn.com/${countryCodeAlpha2.toLocaleLowerCase()}.svg`}
                  alt={`${countryName} flaga`}
                  layout="fill"
                  objectFit="cover"
                />
              </div>
              <p className="text-[12px] font-bold overflow-ellipsis line-clamp-1 text-left">{countryName}</p>
            </div>
            <ChevronDownIcon
              width={13}
              strokeWidth={3}
              className={cn('text-primary-600 transition-transform duration-300 ease-in-out', open && 'rotate-180')}
            />
          </div>
        </Disclosure.Button>
        <div
          className={`transition-all duration-[350ms] ease-in-out overflow-hidden ${
            open ? 'max-h-[500000px] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <ul className="list-none px-4 space-y-3 mt-4">
            {competitions.map(({ id, name, slug }) => (
              <li
                key={`competition-${id}`}
                className="border-b dark:border-gray-650 border-white_theme-100 pb-3 last:border-b-0 animate-fade-in"
              >
                <LivescoreEntityLink
                  href={`/livescore/league/${slug ?? id}`}
                  className="text-[10px] dark:text-gray-400 text-gray-600"
                >
                  {name}
                </LivescoreEntityLink>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Disclosure>
  );
};

const AllCompetitionsCard = () => {
  const { competitions } = useLivescoreLayout();

  const [search, setSearch] = useState('');
  const [filtered, setFiltered] = useState(competitions.all);
  const [configuredCountries, setConfiguredCountries] = useState<string[]>([]);

  useEffect(() => {
    async function fetchCountriesConfig() {
      try {
        const response = await fetch('/api/livescore/countries-config');
        const data = await response.json();
        if (data.success && Array.isArray(data.data?.countries)) {
          setConfiguredCountries(data.data.countries);
        }
      } catch {
        // silent fail — fallback to showing all
      }
    }
    fetchCountriesConfig();
  }, []);

  useEffect(() => {
    if (search) {
      const newFiltered = Object.fromEntries(
        Object.entries(competitions.all)
          .map(([key, comps]) => {
            const filteredCompetitions = comps.filter((c) => c.name.toLowerCase().includes(search.toLowerCase()));
            return [key, filteredCompetitions];
          })
          .filter(([_, comps]) => comps.length > 0),
      );
      setFiltered(newFiltered);
    } else if (configuredCountries.length > 0) {
      const newFiltered = Object.fromEntries(
        Object.entries(competitions.all).filter(([key]) => configuredCountries.includes(key)),
      );
      setFiltered(newFiltered);
    } else {
      setFiltered(competitions.all);
    }
  }, [search, competitions.all, configuredCountries]);

  return (
    <DividerCard>
      <DividerCardTitle>Wszystkie ligi</DividerCardTitle>
      <DividerCardBody>
        <Input
          placeholder="Wyszukaj ligę"
          id="search"
          className="mb-3 !border-0 -mt-1"
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
        />
        <ul className="list-none px-0 space-y-3">
          {Object.keys(filtered).slice(0,5).sort((a,b) => {
            if( search === '' ) return 0;

            return a.localeCompare(b);
          }).map((key) => (
            <li key={key} className="animate-fade-in">
              <CompetitionCountryCollapse countryCode={key} competitions={filtered[key]} />
            </li>
          ))}
        </ul>
      </DividerCardBody>
    </DividerCard>
  );
};

export default AllCompetitionsCard;
